import React, { Fragment, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilterOptionsState, InputAdornment } from '@mui/material';
import cn from 'classnames';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ScrollHandler } from 'react-virtualized';
/* eslint-disable-next-line no-restricted-imports*/
import MUIAutocomplete, {
  AutocompleteProps,
  AutocompleteRenderOptionState,
  AutocompleteRenderGroupParams,
  AutocompleteRenderGetTagProps,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import { ChipTypeMap } from '@mui/material/Chip/Chip';
import TextField, { Props as TextFieldProps } from '../TextField';
import ListItemButton from '../../ListItemButton';
import { createSx } from '../../../utils';
import Chip from '../../Chip';
import ListBoxWithAutocomplete from './components/ListBoxWithAutocomplete';
import GroupTitle from './components/GroupTitle';
import overrides from './overrides';

const defaultChipProps = overrides!.defaultProps!.slotProps!.chip as ChipTypeMap['props'];

const sx = createSx({
  root: {
    display: 'inline-flex',
    position: 'absolute',
    right: 1,
  },
});

export interface DefaultOption<T = number> {
  value: T;
  label: string;
}

type ChipComponentProps = Partial<typeof defaultChipProps>
  & ReturnType<AutocompleteRenderGetTagProps>;

type RenderTag<T> = (
  componentProps: ChipComponentProps,
  option: T,
  state: {
    disabled?: boolean;
    clickable?: boolean;
  },
) => React.ReactNode;

export interface AutocompleteBaseProps<
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> extends Omit<AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  | 'renderInput'
  | 'renderOption'
> {
  options: T[];
  name: string;
  loading?: boolean;
  error?: boolean;
  helperText?: TextFieldProps['helperText'];
  hideHelperText?: boolean;
  label?: string;
  onClear?: () => void;
  onOptionsListScroll?: ScrollHandler;
  required?: boolean;
  startAdornment?: React.ReactNode;
  testID?: string;
  populateValueToOptions?: boolean;
  autoFocus?: boolean;
  async?: boolean;
  renderOption?: RenderOption<T>;
  renderTag?: RenderTag<T>;
}

export type RenderOption<OptionT> = (
  option: OptionT,
  state: AutocompleteRenderOptionState & { disabled?: boolean },
) => React.ReactNode;

function instanceOfDefaultOption(object: unknown): object is DefaultOption {
  return !!(object && typeof object === 'object' && 'label' in object);
}

const AutocompleteContext = React.createContext<{
  onOptionsListScroll?: ScrollHandler;
  loading?: boolean;
  isGrouped?: boolean;
} | null>(null);

export const useAutocompleteContext = () => useContext(AutocompleteContext)!;

const AutocompleteBase = <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({
    disableClearable,
    placeholder,
    error,
    helperText,
    hideHelperText,
    label,
    loading,
    name,
    onClear,
    onOptionsListScroll,
    disabled,
    required,
    readOnly,
    startAdornment,
    testID,
    populateValueToOptions,
    autoFocus,
    async,
    renderOption: customRenderOption,
    renderTag,
    ...props
  }: AutocompleteBaseProps<T, Multiple, DisableClearable, FreeSolo>) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const hasValue = !!(!props.multiple ? props.value : (props.value as T[]).length);
  const showClearIcon = !disableClearable && onClear && hasValue && !disabled && !readOnly;

  const renderAdornment = () => (
    <InputAdornment disablePointerEvents={disabled} sx={sx.root} position='end'>
      {showClearIcon && <CancelIcon
        className='clear-icon'
        data-test-id={`${name || testID || 'autocomplete'}-clear-icon`}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      />}
      <ErrorIcon
        className='error-icon'
        data-test-id={`${name || testID || 'autocomplete'}-error-icon`}
      />
      <ArrowDropDownIcon
        data-test-id={`${name || testID || 'autocomplete'}-arrow-btn`}
        className={cn('arrow-drop-down-icon', { open })}
        onClick={readOnly ? undefined : () => setOpen(prev => !prev)}
      />
    </InputAdornment>
  );

  type DefaultRenderOption = AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>['renderOption'];

  const getOptionLabel = (option: T) => {
    const isDefaultOption = instanceOfDefaultOption(option);

    if (!isDefaultOption && !props.getOptionLabel && !customRenderOption) {
      throw new Error('Cannot render option. Please, pass getOptionLabel method in props1');
    }

    if (props.getOptionLabel) return props.getOptionLabel(option);
    else if (isDefaultOption) return option.label;
  };

  const renderOption: DefaultRenderOption = ({ className: _, onClick, ...elementProps }, option, state) => {
    const optionLabel = getOptionLabel(option);

    const disabled = props.getOptionDisabled?.(option);

    const optionContent = customRenderOption ?
      customRenderOption(option, { ...state, disabled })
      : <ListItemButton
        disabled={disabled}
        label={optionLabel ?? ''}
        selected={state.selected}
      />;

    return <li {...elementProps} onClick={disabled ? undefined : onClick}>
      {optionContent}
    </li>;
  };

  const renderGroup = (params: AutocompleteRenderGroupParams) => {
    return (
      <Fragment key={params.group}>
        <GroupTitle>{params.group}</GroupTitle>
        {params.children}
      </Fragment>
    );
  };

  const options = useMemo(() => {
    if (!populateValueToOptions) return props.options;
    if (Array.isArray(props.value)) return [...props.value, ...props.options];
    return props.value ? [props.value, ...props.options] : props.options;
  }, [props.value, props.options, populateValueToOptions]);

  const hidePopulatedOptions = (autocompleteOptions: T[], state: FilterOptionsState<T>) => {

    const getUpdatedOptions = () => {
      if (Array.isArray(props.value)) {
        return autocompleteOptions.slice(props.value.length);
      }

      if (props.value) {
        return autocompleteOptions.slice(1);
      }

      return autocompleteOptions;
    };

    const updatedOptions = getUpdatedOptions();
    return props.filterOptions ? props.filterOptions(updatedOptions, state) : updatedOptions;
  };

  const filterOptionsFn = async ? props.filterOptions || ((options: T[]) => options)
    : props.filterOptions;

  return (
    <AutocompleteContext.Provider value={{ onOptionsListScroll, loading, isGrouped: !!props.groupBy }}>
      <MUIAutocomplete
        {...props}
        options={options}
        filterOptions={populateValueToOptions ? hidePopulatedOptions : filterOptionsFn}
        disabled={disabled}
        open={open}
        disableClearable={disableClearable}
        disableCloseOnSelect={props.multiple}
        onOpen={() => !readOnly && setOpen(true)}
        onClose={() => setOpen(false)}
        renderOption={renderOption}
        slotProps={{
          listbox: {
            component: ListBoxWithAutocomplete,
          },
          chip: {
            ...props.slotProps?.chip,
            ...defaultChipProps,
            color: error ? 'error' : undefined,
          },
        }}
        readOnly={readOnly}
        blurOnSelect={!props.multiple}
        loading={loading}
        loadingText={t('loading')}
        renderGroup={props.renderGroup || renderGroup}
        renderTags={props.renderTags || ((value, getTagProps) => {
          return value.map((option, index) => {
            const isChipDisabled = props.getOptionDisabled?.(option) || disabled;
            const chipProps = {
              ...defaultChipProps,
              ...getTagProps({ index }),
            };
            if (renderTag) return renderTag(chipProps, option, {
              disabled: isChipDisabled,
              clickable: !isChipDisabled
            });
            const label = getOptionLabel(option);
            return (
              <Chip
                {...chipProps}
                key={index} // eslint-disable-line react/no-array-index-key
                label={label}
                disabled={isChipDisabled}
                clickable={!isChipDisabled}
              />
            );
          });
        })}
        renderInput={({ InputLabelProps: _, ...params }) => {
          const { InputProps, inputProps, ...restParams } = params;
          let startAdornmentElement = startAdornment || InputProps.startAdornment;

          if (props.multiple && startAdornment && InputProps.startAdornment) {
            startAdornmentElement = (
              <>
                {startAdornment}
                <Box component='span' ml={0.5} />
                {InputProps.startAdornment}
              </>
            );
          }

          return <TextField
            {...restParams}
            autoFocus={autoFocus}
            name={name}
            variant='outlined'
            slotProps={{
              input: {
                ...InputProps,
                startAdornment: startAdornmentElement,
                endAdornment: renderAdornment(),
              },
              htmlInput: {
                ...inputProps
              }
            }}
            required={required}
            placeholder={hasValue ? undefined : placeholder}
            error={error}
            helperText={helperText}
            hideHelperText={hideHelperText}
            label={label}
            loading={loading}
            readOnly={readOnly}
            testID={testID}
            onClear={onClear}
          />;
        }}
      />
    </AutocompleteContext.Provider>

  );
};

export default AutocompleteBase;